import {parseISO, isValid} from 'date-fns';

/**
 * Etichetta dell'anno scolastico (es. "2025/2026") derivata dalle date di un'istanza di
 * servizio. Helper PURO, FE-only: nessun endpoint nuovo (vedi change
 * `cittadino-mensa-home-riepilogo`).
 *
 * `dataFine` può MANCARE (servizio senza scadenza → `null`): in quel caso l'anno scolastico
 * è derivato dalla sola `dataInizio` (`AAAA/AAAA+1`). Se nemmeno `dataInizio` è valida,
 * ritorna `null` (l'etichetta va omessa, niente segnaposto).
 *
 * Regola: l'anno scolastico parte dall'anno di `dataInizio`. Es. inizio 2025-09-01 → "2025/2026".
 */
export function annoScolasticoLabel(
  dataInizio: string | null | undefined,
  dataFine: string | null | undefined,
): string | null {
  const inizio = dataInizio ? parseISO(dataInizio) : null;
  if (inizio && isValid(inizio)) {
    const anno = inizio.getFullYear();
    return `${anno}/${anno + 1}`;
  }
  // Fallback: nessun inizio valido ma una fine valida → deriva a ritroso (AAAA-1/AAAA).
  const fine = dataFine ? parseISO(dataFine) : null;
  if (fine && isValid(fine)) {
    const anno = fine.getFullYear();
    return `${anno - 1}/${anno}`;
  }
  return null;
}

/** Stato del ciclo di vita di un'istanza di servizio, derivato dalle sue date. */
export type StatoServizio = 'imminente' | 'attivo' | 'terminato';

/**
 * Stato del ciclo di vita di un'istanza di servizio, come SEGNALE DI CONTESTO PURO
 * (non un gating): calcolato solo da `(dataInizio, dataFine, oggi)`. Helper PURO,
 * FE-only, nessun endpoint (vedi change `cittadino-mensa-ciclo-vita-servizio`).
 *
 * Le date sono stringhe `YYYY-MM-DD` confrontate LESSICOGRAFICAMENTE con `oggi` (stesso
 * formato/convenzione di `ServiziAttiviService.oggiIso()`/`servizioCorrenteDiTipo$`):
 * niente parsing, niente fuso orario. `oggi` è INIETTATO dal chiamante → testabile.
 *
 * Regole:
 *  - `oggi < dataInizio` → `imminente`;
 *  - `dataInizio ≤ oggi ≤ dataFine` (estremi inclusi) → `attivo`;
 *  - `oggi > dataFine` → `terminato`;
 *  - `dataFine` `null`/assente = servizio senza scadenza → MAI `terminato`;
 *  - `dataInizio` assente/vuota → `attivo` (non blocchiamo su dati incompleti, coerente
 *    col comportamento decorativo dell'etichetta annualità).
 */
export function statoServizio(
  dataInizio: string | null | undefined,
  dataFine: string | null | undefined,
  oggi: string,
): StatoServizio {
  if (dataInizio && oggi < dataInizio) {
    return 'imminente';
  }
  if (dataFine && oggi > dataFine) {
    return 'terminato';
  }
  return 'attivo';
}
